
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface GlossaryTermDef {
  pattern: RegExp;
  slug: string;
  tooltipTerm: string;
  definition: string;
}

const GLOSSARY_TERMS: GlossaryTermDef[] = [
  // Multi-word specific phrases first
  {
    pattern: /\bAI\s+Readiness\s+Evaluation\b/i,
    slug: 'ai-readiness-audit',
    tooltipTerm: 'AI Readiness Audit',
    definition: 'A structured assessment of an organisation\'s current workflows, data maturity, and operational readiness to adopt AI effectively.',
  },
  {
    pattern: /\bAI\s+readiness\s+assess(?:ment|ing)\b/i,
    slug: 'ai-readiness-audit',
    tooltipTerm: 'AI Readiness Audit',
    definition: 'A structured assessment of an organisation\'s current workflows, data maturity, and operational readiness to adopt AI effectively.',
  },
  {
    pattern: /\bAI\s+readiness\s+audit\b/i,
    slug: 'ai-readiness-audit',
    tooltipTerm: 'AI Readiness Audit',
    definition: 'A structured assessment of an organisation\'s current workflows, data maturity, and operational readiness to adopt AI effectively.',
  },
  {
    pattern: /\bhuman-in-the-loop\b/i,
    slug: 'human-in-the-loop-ai',
    tooltipTerm: 'Human-in-the-Loop AI',
    definition: 'An AI design pattern where humans retain decision-making authority at critical points in an automated workflow.',
  },
  {
    pattern: /\bhuman\s+oversight\b/i,
    slug: 'human-in-the-loop-ai',
    tooltipTerm: 'Human-in-the-Loop AI',
    definition: 'An AI design pattern where humans retain decision-making authority at critical points in an automated workflow.',
  },
  {
    pattern: /\bgoverned\s+AI\s+assistant\b/i,
    slug: 'governed-ai-assistant',
    tooltipTerm: 'Governed AI Assistant',
    definition: 'A managed AI assistant configured with governance rules, quality assurance testing, and escalation workflows.',
  },
  {
    pattern: /\bgovernance\s+frameworks?\b/i,
    slug: 'ai-governance',
    tooltipTerm: 'AI Governance',
    definition: 'The set of policies, principles, and oversight structures that guide responsible development, deployment, and monitoring of AI systems.',
  },
  {
    pattern: /\bprofessional\s+services?\s+automation\b/i,
    slug: 'professional-services-automation',
    tooltipTerm: 'Professional Services Automation',
    definition: 'Technology solutions designed for professional service firms to automate operational processes including project management, resource allocation, and billing.',
  },
  {
    pattern: /\bproject\s+workflow\s+automation\b/i,
    slug: 'project-workflow-automation',
    tooltipTerm: 'Project Workflow Automation',
    definition: 'The automation of recurring project management tasks and information flows within project-based organisations.',
  },
  {
    pattern: /\bproposal\s+automation\b/i,
    slug: 'proposal-automation',
    tooltipTerm: 'Proposal Automation',
    definition: 'The use of technology to streamline the process of creating, pricing, and delivering business proposals, drawing on historical data.',
  },
  {
    pattern: /\bworkflow\s+intelligence\b/i,
    slug: 'workflow-intelligence',
    tooltipTerm: 'Workflow Intelligence',
    definition: 'The capability to analyse workflow data, identify patterns, and generate actionable insights that improve how work flows through an organisation.',
  },
  {
    pattern: /\boperational\s+intelligence\b/i,
    slug: 'operational-intelligence',
    tooltipTerm: 'Operational Intelligence',
    definition: 'The capability to collect, analyse, and act on real-time operational data to improve business performance.',
  },
  {
    pattern: /\bAI\s+adoption\s+roadmap\b/i,
    slug: 'ai-adoption-strategy',
    tooltipTerm: 'AI Adoption Strategy',
    definition: 'A phased plan that outlines how an organisation will evaluate, pilot, and scale AI solutions aligned with its business objectives.',
  },
  {
    pattern: /\bAI\s+adoption\s+strateg(?:y|ies)\b/i,
    slug: 'ai-adoption-strategy',
    tooltipTerm: 'AI Adoption Strategy',
    definition: 'A phased plan that outlines how an organisation will evaluate, pilot, and scale AI solutions aligned with its business objectives.',
  },
  {
    pattern: /\bAI\s+roadmap\b/i,
    slug: 'ai-adoption-strategy',
    tooltipTerm: 'AI Adoption Strategy',
    definition: 'A phased plan that outlines how an organisation will evaluate, pilot, and scale AI solutions aligned with its business objectives.',
  },
  {
    pattern: /\bAI\s+decision\s+support\b/i,
    slug: 'ai-decision-support',
    tooltipTerm: 'AI Decision Support',
    definition: 'Systems that use AI to provide relevant data, analysis, and recommendations to human decision-makers.',
  },
  {
    pattern: /\bdecision\s+intelligence\b/i,
    slug: 'decision-intelligence',
    tooltipTerm: 'Decision Intelligence',
    definition: 'The application of data analysis, AI models, and structured frameworks to improve the quality and consistency of business decisions.',
  },
  {
    pattern: /\bautomation\s+ROI\b/i,
    slug: 'automation-roi',
    tooltipTerm: 'Automation ROI',
    definition: 'The measurable return on investment from automation initiatives, comparing implementation costs against quantifiable benefits.',
  },
  {
    pattern: /\bautomation\s+strateg(?:y|ies)\b/i,
    slug: 'automation-strategy',
    tooltipTerm: 'Automation Strategy',
    definition: 'A structured plan that identifies which business processes should be automated, in what order, and with what expected outcomes.',
  },
  {
    pattern: /\bdata\s+workflows?\b/i,
    slug: 'data-workflow',
    tooltipTerm: 'Data Workflow',
    definition: 'The structured path data follows through an organisation\'s systems — from capture through processing, storage, and use in decision-making.',
  },
  {
    pattern: /\bAI\s+integrations?\b/i,
    slug: 'ai-integration',
    tooltipTerm: 'AI Integration',
    definition: 'The process of connecting AI tools with existing business systems and workflows so they function as a cohesive environment.',
  },
  {
    pattern: /\bworkflow\s+and\s+process\s+mapping\b/i,
    slug: 'process-mapping',
    tooltipTerm: 'Process Mapping',
    definition: 'A visual documentation technique that captures each step, decision point, and handoff in a business workflow to identify inefficiencies.',
  },
  {
    pattern: /\bprocess\s+mapping\b/i,
    slug: 'process-mapping',
    tooltipTerm: 'Process Mapping',
    definition: 'A visual documentation technique that captures each step, decision point, and handoff in a business workflow to identify inefficiencies.',
  },
  {
    pattern: /\bprocess\s+automation\b/i,
    slug: 'process-automation',
    tooltipTerm: 'Process Automation',
    definition: 'The use of technology to execute defined business processes automatically, reducing manual effort and human error.',
  },
  {
    pattern: /\bworkflow\s+automation\b/i,
    slug: 'workflow-automation',
    tooltipTerm: 'Workflow Automation',
    definition: 'The use of technology to perform recurring business tasks automatically, reducing manual effort and human error.',
  },
  {
    pattern: /\bAI\s+automation\b/i,
    slug: 'ai-automation',
    tooltipTerm: 'AI Automation',
    definition: 'The use of artificial intelligence to perform tasks that traditionally required human decision-making.',
  },
  {
    pattern: /\bAI\s+advisory\b/i,
    slug: 'ai-advisory',
    tooltipTerm: 'AI Advisory',
    definition: 'Professional guidance that helps organisations evaluate, plan, and implement AI solutions aligned with their specific business objectives.',
  },
  {
    pattern: /\bAI\s+agents?\b/i,
    slug: 'ai-agent',
    tooltipTerm: 'AI Agent',
    definition: 'A software system that perceives its environment, makes decisions, and takes autonomous actions to achieve specific goals.',
  },
  {
    pattern: /\bAI\s+assistants?\b/i,
    slug: 'ai-agent',
    tooltipTerm: 'AI Agent',
    definition: 'A software system that perceives its environment, makes decisions, and takes autonomous actions to achieve specific goals.',
  },
  {
    pattern: /\bAI\s+adoption\b/i,
    slug: 'ai-adoption',
    tooltipTerm: 'AI Adoption',
    definition: 'The process by which an organisation begins using AI tools and practices within its operations.',
  },
  {
    pattern: /\boperational\s+visibility\b/i,
    slug: 'operational-visibility',
    tooltipTerm: 'Operational Visibility',
    definition: 'The ability to see what is happening across all key business processes in real time.',
  },
  {
    pattern: /\bAI\s+governance\b/i,
    slug: 'ai-governance',
    tooltipTerm: 'AI Governance',
    definition: 'The set of policies, principles, and oversight structures that guide responsible development, deployment, and monitoring of AI systems.',
  },
  {
    pattern: /\bgovernance\b/i,
    slug: 'ai-governance',
    tooltipTerm: 'AI Governance',
    definition: 'The set of policies, principles, and oversight structures that guide responsible development, deployment, and monitoring of AI systems.',
  },
];

// Sorted longest pattern source first to avoid partial matches
const SORTED_TERMS = [...GLOSSARY_TERMS].sort(
  (a, b) => b.pattern.source.length - a.pattern.source.length
);

const SKIP_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'button', 'a', 'nav']);

function shouldSkip(element: React.ReactElement): boolean {
  const type = element.type;
  if (typeof type === 'string' && SKIP_TAGS.has(type.toLowerCase())) return true;
  return false;
}

function GlossaryIcon({ term }: { term: GlossaryTermDef }) {
  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <BookOpen
            size={14}
            className="inline-flex align-baseline ml-1 text-muted-foreground hover:text-brand-teal transition-colors duration-150 cursor-pointer"
            aria-hidden="true"
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-sm">
          <p className="font-semibold mb-1">{term.tooltipTerm}</p>
          <p className="text-muted-foreground">{term.definition}</p>
          <p className="text-brand-teal mt-1 text-xs font-medium">View full definition →</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface GlossaryAutoLinkerProps {
  children: React.ReactNode;
  maxLinks?: number;
}

const GlossaryAutoLinker: React.FC<GlossaryAutoLinkerProps> = ({ children, maxLinks }) => {
  const { pathname } = useLocation();

  // Reset matched set on each render cycle (component is used per-section)
  const matched = useMemo(() => new Set<string>(), []);
  let linkCount = 0;

  if (pathname === '/glossary') {
    return <>{children}</>;
  }

  function processText(text: string): React.ReactNode[] {
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      if (maxLinks !== undefined && linkCount >= maxLinks) {
        parts.push(remaining);
        break;
      }

      let earliestMatch: { index: number; length: number; term: GlossaryTermDef } | null = null;

      for (const term of SORTED_TERMS) {
        if (matched.has(term.slug)) continue;
        const match = remaining.match(term.pattern);
        if (match && match.index !== undefined) {
          if (!earliestMatch || match.index < earliestMatch.index) {
            earliestMatch = { index: match.index, length: match[0].length, term };
          }
        }
      }

      if (!earliestMatch) {
        parts.push(remaining);
        break;
      }

      // Text before the match
      if (earliestMatch.index > 0) {
        parts.push(remaining.slice(0, earliestMatch.index));
      }

      const matchedText = remaining.slice(earliestMatch.index, earliestMatch.index + earliestMatch.length);
      matched.add(earliestMatch.term.slug);
      linkCount++;

      parts.push(
        <Link
          key={`gl-${key++}`}
          to={`/glossary#${earliestMatch.term.slug}`}
          className="inline-items-baseline no-underline text-inherit hover:text-inherit"
          aria-label={`Definition: ${earliestMatch.term.tooltipTerm}`}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {matchedText}
          <GlossaryIcon term={earliestMatch.term} />
        </Link>
      );

      remaining = remaining.slice(earliestMatch.index + earliestMatch.length);
    }

    return parts;
  }

  function traverse(node: React.ReactNode): React.ReactNode {
    if (maxLinks !== undefined && linkCount >= maxLinks) {
      return node;
    }

    if (typeof node === 'string') {
      const processed = processText(node);
      return processed.length === 1 && typeof processed[0] === 'string' ? processed[0] : <>{processed}</>;
    }

    if (typeof node === 'number' || typeof node === 'boolean' || node === null || node === undefined) {
      return node;
    }

    if (Array.isArray(node)) {
      return node.map((child, i) => <React.Fragment key={i}>{traverse(child)}</React.Fragment>);
    }

    if (React.isValidElement(node)) {
      if (shouldSkip(node as React.ReactElement)) {
        return node;
      }
      const element = node as React.ReactElement<{ children?: React.ReactNode }>;
      if (element.props.children) {
        return React.cloneElement(element, {}, traverse(element.props.children));
      }
      return node;
    }

    return node;
  }

  return <>{traverse(children)}</>;
};

export default GlossaryAutoLinker;
